import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import ActionButton from 'atoms/ActionButton';
import Table from 'atoms/Table';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import ImageDetailsCell from 'atoms/TableCell/ImageDetailsCell';
import ValidateSentModal from 'components/ValidateSentModal';
import { headerStyle } from 'containers/App/constants';
import { downloadFile } from 'containers/App/actions';
import { makeSelectIsMobile } from 'containers/App/selectors';
import hideImage from 'svgs/hide';
import searchImage from 'svgs/search';
import addImage from 'svgs/add';
import api from 'utils/api';
import history from 'utils/history';

import {
	fetchAdminRois,
	adminRevokeRoi,
	approveRoiMatch,
	rejectRoiMatches,
	adminLinkRoi,
} from './actions';
import {
	makeSelectAdminRois,
	makeSelectAdminRoisLoading,
	makeSelectAdminRoisSubmitting,
} from './selectors';

const statusBadge = (status) => {
	const colors = {
		matched: 'badge-success',
		awaiting_signature: 'badge-info',
		active: 'badge-warning',
		pending_review: 'badge-warning',
		revoked: 'badge-danger',
		expired: 'badge-secondary',
	};
	const cls = colors[status] || 'badge-secondary';
	const label = (status || '').replace(/_/g, ' ');
	return (
		<span className={`badge ${cls} font-10 px-2 py-1 text-uppercase`}>
			{label}
		</span>
	);
};

const STATUS_OPTIONS = [
	{ value: '', label: 'All Statuses' },
	{ value: 'matched', label: 'Matched' },
	{ value: 'awaiting_signature', label: 'Awaiting Signature' },
	{ value: 'active', label: 'Active' },
	{ value: 'pending_review', label: 'Pending Review' },
	{ value: 'revoked', label: 'Revoked' },
	{ value: 'expired', label: 'Expired' },
];

class AdminRoiList extends Component {
	state = {
		statusFilter: '',
		showRevokeModal: false,
		showLinkModal: false,
		showReviewModal: false,
		reviewRoi: null,
		activeRoiId: null,
		referrerSearchQuery: '',
		referrerSearchResults: [],
		referrerSearchLoading: false,
		selectedReferrerId: null,
	};

	componentDidMount() {
		this.loadRois();
	}

	loadRois = () => {
		const params = {};
		if (this.state.statusFilter) {
			params.status = this.state.statusFilter;
		}
		this.props.fetchRois(params);
	};

	handleStatusFilter = (e) => {
		this.setState({ statusFilter: e.target.value }, this.loadRois);
	};

	goToProfile = (id) => {
		if (id) history.push(`profile/${id}`);
	};

	handleRevokeClick = (roiId) => {
		this.setState({ showRevokeModal: true, activeRoiId: roiId });
	};

	handleConfirmRevoke = () => {
		this.props.revokeRoi({
			roiId: this.state.activeRoiId,
			reason: 'Revoked by admin',
		});
	};

	handleReferrerSearch = async () => {
		const { referrerSearchQuery } = this.state;
		if (!referrerSearchQuery.trim()) return;
		this.setState({ referrerSearchLoading: true });
		try {
			const response = await api.get('/admin/referrers', {
				params: { q: referrerSearchQuery },
			});
			this.setState({
				referrerSearchResults: response.data?.data?.referrers || [],
				referrerSearchLoading: false,
			});
		} catch (err) {
			this.setState({
				referrerSearchResults: [],
				referrerSearchLoading: false,
			});
		}
	};

	handleConfirmLink = () => {
		this.props.linkRoi({
			roiId: this.state.activeRoiId,
			referrerId: this.state.selectedReferrerId,
		});
	};

	componentDidUpdate(prevProps) {
		if (prevProps.submitting && !this.props.submitting) {
			if (this.state.showRevokeModal) {
				this.setState({ showRevokeModal: false, activeRoiId: null });
			}
			if (this.state.showLinkModal) {
				this.setState({
					showLinkModal: false,
					activeRoiId: null,
					referrerSearchQuery: '',
					referrerSearchResults: [],
					selectedReferrerId: null,
				});
			}
		}
	}

	getColumns = () => [
		{
			field: 'patient',
			title: 'CLIENT',
			headerStyle,
			render: (row) => (
				<div className="hand" onClick={() => this.goToProfile(row.patientUser?.id || row.patient?.id)}>
					<ImageDetailsCell
						heading={row.patientUser?.name || `Patient #${row.patient?.id || '—'}`}
						primarySubHeading={row.patientUser?.email || ''}
						className="mt-2 mx-2"
					/>
				</div>
			),
		},
		{
			field: 'clinicianName',
			title: 'RECIPIENT',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2 font-13">
					{row.clinicianName || '—'}
				</div>
			),
		},
		{
			field: 'createdAt',
			title: 'SUBMITTED ON',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2 font-13">
					{moment(row.createdAt).format('YYYY-MM-DD')}
				</div>
			),
		},
		{
			field: 'status',
			title: 'STATUS',
			headerStyle,
			render: (row) => (
				<div className="mt-2 mx-2">{statusBadge(row.status)}</div>
			),
		},
		{
			field: 'document',
			title: 'DOCUMENT',
			headerStyle,
			render: (row) => {
				const uuid = row.userDocumentId?.uuid;
				if (!uuid) return <div className="mt-2 mx-2 font-13 text-muted">—</div>;
				return (
					<div
						className="mt-2 mx-2 font-13 hand link-color"
						onClick={() => this.props.downloadFile(uuid)}
					>
						DOWNLOAD
					</div>
				);
			},
		},
		{
			field: 'actions',
			title: '',
			headerStyle: { ...headerStyle, textAlign: 'right' },
			render: (row) => {
				const canRevoke =
					row.status === 'matched' ||
					row.status === 'active' ||
					row.status === 'pending_review';
				const submitting = this.props.submitting;
				return (
					<div className="row justify-content-end mr-2">
						{row.status === 'pending_review' && row.candidates?.length > 0 && (
							<div className="col-auto px-0">
								<ActionButton
									image={searchImage}
									hoverColor={submitting ? 'var(--main-light-grey)' : 'var(--main-color)'}
									fillColor={submitting ? 'var(--main-light-grey)' : 'var(--main-grey)'}
									onClick={() => this.setState({ showReviewModal: true, reviewRoi: row })}
									alt={submitting ? 'Loading...' : 'Review Candidates'}
									buttonStyle={submitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
								/>
							</div>
						)}
						{row.status === 'active' && (
							<div className="col-auto px-0">
								<ActionButton
									image={addImage}
									hoverColor={submitting ? 'var(--main-light-grey)' : 'var(--main-color)'}
									fillColor={submitting ? 'var(--main-light-grey)' : 'var(--main-grey)'}
									onClick={() => this.setState({ showLinkModal: true, activeRoiId: row.id })}
									alt={submitting ? 'Loading...' : 'Link'}
									buttonStyle={submitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
								/>
							</div>
						)}
						{canRevoke && (
							<div className="col-auto px-0">
								<ActionButton
									image={hideImage}
									hoverColor={submitting ? 'var(--main-light-grey)' : 'var(--main-alert)'}
									fillColor={submitting ? 'var(--main-light-grey)' : 'var(--main-grey)'}
									onClick={() => this.handleRevokeClick(row.id)}
									alt={submitting ? 'Loading...' : 'Revoke'}
									buttonStyle={submitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
								/>
							</div>
						)}
					</div>
				);
			},
		},
	];

	renderMobile = (rois) =>
		rois.map((roi) => (
			<div key={roi.id} className="card mb-2 p-3">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<span
						className="font-13 font-weight-bold hand link-color"
						onClick={() => this.goToProfile(roi.patientUser?.id || roi.patient?.id)}
					>
						{roi.patientUser?.name || `Patient #${roi.patient?.id || '—'}`}
					</span>
					{statusBadge(roi.status)}
				</div>
				<div className="font-11 text-muted mb-1">
					Recipient: {roi.clinicianName || '—'}
				</div>
				<div className="font-11 text-muted mb-1">
					Submitted: {moment(roi.createdAt).format('YYYY-MM-DD')}
				</div>
				{roi.userDocumentId?.uuid && (
					<div
						className="font-11 hand link-color mb-1"
						onClick={() => this.props.downloadFile(roi.userDocumentId.uuid)}
					>
						DOWNLOAD DOCUMENT
					</div>
				)}
				{(roi.status === 'matched' ||
					roi.status === 'active' ||
					roi.status === 'pending_review') && (
					<button
						className="btn btn-sm btn-outline-danger font-10 text-uppercase mt-2"
						onClick={() => this.handleRevokeClick(roi.id)}
					>
						Revoke
					</button>
				)}
			</div>
		));

	render() {
		const { rois, loading, isMobile } = this.props;
		const { statusFilter, showRevokeModal } = this.state;

		return (
			<div>
				<div className="d-flex justify-content-end mb-2 px-2">
					<select
						className="form-control form-control-sm font-12"
						style={{ width: 'auto' }}
						value={statusFilter}
						onChange={this.handleStatusFilter}
					>
						{STATUS_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>
				{isMobile ? (
					<div className="row mx-1">
						{loading ? (
							<SmallLoadingIndicator
								text={'Loading...'}
								color="var(--main-color)"
							/>
						) : (
							this.renderMobile(rois)
						)}
					</div>
				) : (
					<Table
						keyField="adminRoiList"
						data={rois}
						columns={this.getColumns()}
						loading={loading}
						placeholder="No ROI records found"
						classes="table-responsive-xl"
					/>
				)}
				{showRevokeModal && (
					<ValidateSentModal
						hide={() =>
							this.setState({ showRevokeModal: false, activeRoiId: null })
						}
						handleDelete={this.handleConfirmRevoke}
						message="You are revoking this ROI. This action is permanent and cannot be undone."
						toBeDeleted={{ name: 'REVOKE' }}
					/>
				)}
				{this.state.showLinkModal && (
					<div
						className="modal d-block"
						style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
					>
						<div className="modal-dialog">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">
										Link ROI to Referrer
									</h5>
									<button
										type="button"
										className="close"
										onClick={() =>
											this.setState({
												showLinkModal: false,
												activeRoiId: null,
												referrerSearchQuery: '',
												referrerSearchResults: [],
												selectedReferrerId: null,
											})
										}
									>
										<span>&times;</span>
									</button>
								</div>
								<div className="modal-body">
									<div className="input-group mb-3">
										<input
											type="text"
											className="form-control font-13"
											placeholder="Search by name, email, or practice"
											value={
												this.state.referrerSearchQuery
											}
											onChange={(e) =>
												this.setState({
													referrerSearchQuery:
														e.target.value,
												})
											}
											onKeyDown={(e) =>
												e.key === 'Enter' &&
												this.handleReferrerSearch()
											}
										/>
										<div className="input-group-append">
											<button
												className="btn btn-primary font-12"
												onClick={
													this.handleReferrerSearch
												}
												disabled={
													this.state
														.referrerSearchLoading
												}
											>
												{this.state
													.referrerSearchLoading
													? 'Searching...'
													: 'Search'}
											</button>
										</div>
									</div>
									<div
										className="list-group"
										style={{
											maxHeight: 300,
											overflowY: 'auto',
										}}
									>
										{this.state.referrerSearchResults.map(
											(r) => (
												<div
													key={r.id}
													className={`list-group-item list-group-item-action hand font-12 ${
														this.state
															.selectedReferrerId ===
														r.id
															? 'active'
															: ''
													}`}
													onClick={() =>
														this.setState({
															selectedReferrerId:
																r.id,
														})
													}
												>
													<div className="font-weight-bold">
														{r.user?.name || '\u2014'}
													</div>
													<div className="font-11 text-muted">
														{r.user?.email || '\u2014'}
														{r.practiceName &&
															` \u00b7 ${r.practiceName}`}
													</div>
												</div>
											),
										)}
										{this.state.referrerSearchResults
											.length === 0 &&
											!this.state
												.referrerSearchLoading && (
												<div className="text-muted font-12 p-2">
													Search for a referrer to
													link
												</div>
											)}
									</div>
								</div>
								<div className="modal-footer">
									<button
										className="btn btn-secondary font-12"
										onClick={() =>
											this.setState({
												showLinkModal: false,
												activeRoiId: null,
												referrerSearchQuery: '',
												referrerSearchResults: [],
												selectedReferrerId: null,
											})
										}
									>
										Cancel
									</button>
									<button
										className="btn btn-primary font-12"
										disabled={
											!this.state.selectedReferrerId ||
											this.props.submitting
										}
										onClick={this.handleConfirmLink}
									>
										{this.props.submitting
											? 'Linking...'
											: 'Link'}
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
				{this.state.showReviewModal && this.state.reviewRoi && (
					<Modal className="modal-dialog-centered" isOpen toggle={() => this.setState({ showReviewModal: false, reviewRoi: null })}>
						<ModalHeader toggle={() => this.setState({ showReviewModal: false, reviewRoi: null })}>
							Review Match Candidates
						</ModalHeader>
						<ModalBody>
							<div className="mb-3">
								<span className="font-13 font-weight-bold">{this.state.reviewRoi.patientUser?.name}</span>
								<span className="text-muted font-12 ml-2">&rarr; {this.state.reviewRoi.clinicianName}</span>
							</div>
							{(this.state.reviewRoi.candidates || []).map((c) => (
								<div key={c.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
									<div>
										<div className="font-13">{c.referrer?.user?.name || '\u2014'}</div>
										<div className="font-11 text-muted">
											{c.referrer?.user?.email || '\u2014'}
											{c.referrer?.practiceName && ` \u00b7 ${c.referrer.practiceName}`}
										</div>
									</div>
									<button
										className="btn btn-sm btn-outline-success font-10"
										onClick={() => {
											this.props.approveMatch(this.state.reviewRoi.id, c.id);
											this.setState({ showReviewModal: false, reviewRoi: null });
										}}
										disabled={this.props.submitting}
									>
										Approve
									</button>
								</div>
							))}
							<div className="mt-3">
								<button
									className="btn btn-sm btn-outline-danger font-10 text-uppercase"
									onClick={() => {
										this.props.rejectMatches(this.state.reviewRoi.id);
										this.setState({ showReviewModal: false, reviewRoi: null });
									}}
									disabled={this.props.submitting}
								>
									Reject All
								</button>
							</div>
						</ModalBody>
					</Modal>
				)}
			</div>
		);
	}
}

const mapStateToProps = createStructuredSelector({
	rois: makeSelectAdminRois(),
	loading: makeSelectAdminRoisLoading(),
	submitting: makeSelectAdminRoisSubmitting(),
	isMobile: makeSelectIsMobile(),
});

function mapDispatchToProps(dispatch) {
	return {
		fetchRois: (params) => dispatch(fetchAdminRois(params)),
		revokeRoi: (data) => dispatch(adminRevokeRoi(data)),
		downloadFile: (docId) => dispatch(downloadFile(docId)),
		approveMatch: (roiId, candidateId) =>
			dispatch(approveRoiMatch(roiId, candidateId)),
		rejectMatches: (roiId) => dispatch(rejectRoiMatches(roiId)),
		linkRoi: (data) => dispatch(adminLinkRoi(data)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminRoiList);
